// src/pages/Accounting/Reconcile/Summary/SummaryTable.tsx

import type React from "react";
import { PALETTE } from "../constants";
import { formatCurrency, formatDateLong } from "../utils";
import type { ReconciliationSummaryRow } from "./summaryTypes";

interface SummaryTableProps {
  rows: ReconciliationSummaryRow[];
}

export function SummaryTable({ rows }: SummaryTableProps) {
  if (rows.length === 0) {
    return (
      <div style={emptyStyle}>
        No reconcilable accounts yet. Connect a bank or credit card to get started.
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      <table style={tableStyle}>
        <thead>
          <tr style={headRowStyle}>
            <th style={thStyle}>Account</th>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>Statement ending date</th>
            <th style={thStyle}>Reconciled on</th>
            <th style={thNumStyle}>Ending balance</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const neverReconciled = row.statementEndingDate === null;
            return (
              <tr key={row.accountId} style={rowStyle}>
                <td style={{ ...tdStyle, color: PALETTE.ink, fontWeight: 600 }}>
                  {row.accountName}
                </td>
                <td style={tdStyle}>{row.accountType}</td>
                <td
                  style={{
                    ...tdStyle,
                    color: neverReconciled ? PALETTE.ink4 : PALETTE.ink,
                    fontWeight: neverReconciled ? 500 : 500,
                    fontStyle: neverReconciled ? "italic" : "normal",
                  }}
                >
                  {neverReconciled ? "Never" : formatDateLong(row.statementEndingDate!)}
                </td>
                <td
                  style={{
                    ...tdStyle,
                    color: row.reconciledOn === null ? PALETTE.ink4 : PALETTE.ink2,
                  }}
                >
                  {row.reconciledOn === null ? "\u2014" : formatDateLong(row.reconciledOn)}
                </td>
                <td
                  style={{
                    ...tdNumStyle,
                    color: row.endingBalance === null ? PALETTE.ink4 : PALETTE.ink,
                    fontWeight: row.endingBalance === null ? 500 : 600,
                  }}
                >
                  {row.endingBalance === null
                    ? "\u2014"
                    : `${formatCurrency(row.endingBalance, row.currency as never)} ${row.currency}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const wrapStyle: React.CSSProperties = {
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 10,
  overflow: "hidden",
  background: "#fff",
};
const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};
const headRowStyle: React.CSSProperties = {
  background: PALETTE.bgFade,
  borderBottom: `1px solid ${PALETTE.line}`,
};
const thStyle: React.CSSProperties = {
  padding: "12px 16px",
  textAlign: "left",
  color: PALETTE.ink3,
  fontWeight: 600,
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};
const thNumStyle: React.CSSProperties = {
  ...thStyle,
  textAlign: "right",
};
const rowStyle: React.CSSProperties = {
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
};
const tdStyle: React.CSSProperties = {
  padding: "14px 16px",
  color: PALETTE.ink2,
  verticalAlign: "middle",
};
const tdNumStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};
const emptyStyle: React.CSSProperties = {
  padding: "48px 24px",
  textAlign: "center",
  color: PALETTE.ink3,
  fontSize: 13.5,
  fontWeight: 500,
  background: "#fff",
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 10,
};